import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { api } from './api.js';
import { ImageIcon, Spinner, VideoIcon } from './components.jsx';
import { prettySize } from './lib/media.js';

const PAGE = 24;
const when = (iso) => new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

function Tile({ file, src, onOpen }) {
  const [broken, setBroken] = useState(false);
  return (
    <button type="button" onClick={onOpen} className="relative aspect-square overflow-hidden rounded-xl bg-steel/20" aria-label={file.name}>
      {!broken && <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" onError={() => setBroken(true)} />}
      {broken && (
        <span className="flex h-full items-center justify-center text-steel">
          {file.kind === 'video' ? <VideoIcon className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
        </span>
      )}
      {file.kind === 'sketch' && (
        <span className="absolute bottom-1 right-1 rounded-md bg-charcoal/80 px-1.5 py-0.5 text-xs text-white">sketch</span>
      )}
      {file.kind === 'video' && (
        <span className="absolute bottom-1 right-1 flex items-center gap-1 rounded-md bg-charcoal/80 px-1.5 py-0.5 text-xs text-white">
          <VideoIcon className="h-4 w-4" /> video
        </span>
      )}
    </button>
  );
}

function Viewer({ files, index, setIndex, url, onClose, onEdit }) {
  const f = files[index];
  const touch = useRef(null);
  const [fullFallback, setFullFallback] = useState(false);

  useEffect(() => setFullFallback(false), [index]);
  useEffect(() => {
    const key = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index < files.length - 1) setIndex(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
    };
    window.addEventListener('keydown', key);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', key); document.body.style.overflow = prev; };
  }, [index, files.length, onClose, setIndex]);

  const swipe = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    touch.current = null;
    if (Math.abs(dx) < 60) return;
    if (dx < 0 && index < files.length - 1) setIndex(index + 1);
    if (dx > 0 && index > 0) setIndex(index - 1);
  };

  // Portal to <body> so no ancestor can offset or clip the full-screen viewer.
  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="min-w-0 text-sm">
          <div className="truncate font-medium">{f.name}</div>
          <div className="text-xs opacity-70">{when(f.modified)} · {prettySize(f.bytes || 0)} · {index + 1} of {files.length}</div>
        </div>
        <button className="h-11 w-11 shrink-0 rounded-full bg-white/15 text-2xl leading-none" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div
        className="flex min-h-0 flex-1 items-center justify-center"
        onTouchStart={(e) => (touch.current = e.touches[0].clientX)}
        onTouchEnd={swipe}
      >
        {f.kind === 'video' ? (
          <video key={f.name} src={url(f, 'file')} controls autoPlay playsInline preload="metadata" className="max-h-full max-w-full" />
        ) : (
          <img
            key={f.name}
            src={fullFallback ? url(f, 'file') : url(f, 'thumb?size=lg')}
            onError={() => setFullFallback(true)}
            alt={f.name}
            className="max-h-full max-w-full object-contain"
          />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        <button className="btn-ghost !border-white/40 !px-5 !py-2.5 !text-base" disabled={index === 0} onClick={() => setIndex(index - 1)}>‹ Prev</button>
        <span className="flex flex-col items-center gap-1">
          {f.skid && <button type="button" className="rounded-lg bg-amber px-4 py-1.5 text-sm font-bold text-charcoal" onClick={() => onEdit(f)}>Edit sketch</button>}
          <a className="text-sm underline opacity-80" href={url(f, 'file')} target="_blank" rel="noreferrer">Open original</a>
        </span>
        <button className="btn-ghost !border-white/40 !px-5 !py-2.5 !text-base" disabled={index === files.length - 1} onClick={() => setIndex(index + 1)}>Next ›</button>
      </div>
    </div>,
    document.body
  );
}

// Staff only. Files stream through this app from NextCloud; the phone never talks to NextCloud.
export default function MediaGallery({ job }) {
  const [data, setData] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [view, setView] = useState(null);
  const nav = useNavigate();
  const [open, setOpen] = useState(false); // collapsed by default: long galleries push Emails off-screen

  useEffect(() => {
    api.get(`/api/jobs/${job.id}/media`).then(setData).catch(() => setData({ error: true }));
  }, [job.id]);

  const url = (f, what) => `/api/jobs/${job.id}/media/${f.folder}/${encodeURIComponent(f.name)}/${what}`;
  const files = data?.files || [];

  const count = data?.files ? data.photos + data.videos + (data.sketches || 0) : null;

  return (
    <section className="space-y-3">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 py-1 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <h2 className="label !mb-0">Photos &amp; videos{count !== null ? ` (${count})` : ''}</h2>
        <span className="text-steel" aria-hidden="true">{open ? '▾ Hide' : '▸ Show'}</span>
      </button>
      {open && !data && <div className="flex justify-center py-4"><Spinner /></div>}
      {open && data?.error && <p className="text-sm text-steel">Couldn't load the photos just now.</p>}
      {open && data?.files && files.length === 0 && <p className="text-sm text-steel">No photos or videos yet.</p>}
      {open && files.length > 0 && (
        <>
          <div className="grid grid-cols-3 gap-2">
            {files.slice(0, shown).map((f, i) => <Tile key={`${f.folder}/${f.name}`} file={f} src={url(f, 'thumb')} onOpen={() => setView(i)} />)}
          </div>
          {shown < files.length && (
            <button className="btn-ghost w-full !py-2.5 !text-base" onClick={() => setShown(shown + PAGE)}>Show more ({files.length - shown})</button>
          )}
        </>
      )}
      {view !== null && <Viewer files={files} index={view} setIndex={setView} url={url} onClose={() => setView(null)} onEdit={(f) => nav(`/job/${job.id}/sketch?edit=${f.skid}`)} />}
    </section>
  );
}
